import { Box } from '@mui/material';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Switch } from 'components/Switch';
import { TimeField } from '@mui/x-date-pickers';
import PickersTextField from 'components/PickersTextField';
import { Dayjs } from 'dayjs';
import { StyledSettingControl } from 'features/breakout-rooms/components/StyledSettingControl';

export type BreakoutTimerFieldsValues = {
  timer: boolean;
  totalSeconds?: Dayjs;
  stopBreakouts: boolean;
};

type BreakoutTimerFieldsProps = {
  control: Control<BreakoutTimerFieldsValues>;
  errors: FieldErrors<BreakoutTimerFieldsValues>;
  labels?: {
    enable?: string;
    duration?: string;
  };
};

const BreakoutTimerFields = ({ control, errors, labels }: BreakoutTimerFieldsProps) => {
  const { t } = useTranslation('breakout-rooms');
  const timer = useWatch({ control, name: 'timer' });

  return (
    <>
      <StyledSettingControl
        label={labels?.enable ?? t('create_rooms.room_timer.label')}
        alignItems="flex-start"
        control={
          <Controller
            name="timer"
            control={control}
            render={({ field: { value, ref, ...field } }) => (
              <Switch
                {...field}
                slotProps={{
                  input: {
                    ref,
                    'aria-label': value
                      ? t('create_rooms.room_timer.switch_disable_timer_aria_label')
                      : t('create_rooms.room_timer.switch_enable_timer_aria_label'),
                  },
                }}
                checked={value}
              />
            )}
          />
        }
      />
      {timer && (
        <Box sx={{ mt: 6 }}>
          <Box sx={{ mb: 6 }}>
            <StyledSettingControl
              label={labels?.duration ?? t('create_rooms.room_timer.timer_duration_label')}
              control={
                <Controller
                  name="totalSeconds"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      greaterThanZero: (v) => {
                        if (!v) {
                          return false;
                        }
                        const totalMinutes = v.hour() * 60 + v.minute();
                        return totalMinutes > 0;
                      },
                    },
                  }}
                  render={({ field: { ref, ...field } }) => (
                    <TimeField
                      {...field}
                      error={!!errors.totalSeconds}
                      format="HH:mm"
                      inputRef={ref}
                      slots={{ textField: PickersTextField }}
                      slotProps={{
                        textField: {
                          sx: { maxWidth: 150 },
                        },
                      }}
                    />
                  )}
                />
              }
            />
          </Box>
          <StyledSettingControl
            label={t('create_rooms.room_timer.end_on_timer_label')}
            control={
              <Controller
                name="stopBreakouts"
                control={control}
                render={({ field: { value, ref, ...field } }) => (
                  <Switch
                    {...field}
                    slotProps={{
                      input: {
                        ref,
                        'aria-label': value
                          ? t('create_rooms.room_timer.switch_disable_end_on_timer_aria_label')
                          : t('create_rooms.room_timer.switch_enable_end_on_timer_aria_label'),
                      },
                    }}
                    checked={value}
                  />
                )}
              />
            }
          />
        </Box>
      )}
    </>
  );
};

export default BreakoutTimerFields;
